import { EventPageFragment } from "@/__generated__/graphql";
import { ComponentProps } from "@components/ComponentProps";
import Hero, { CreateHeroProps } from "@components/Hero";
import IconLegend from "@components/IconLegend";
import InformationSection from "@components/InformationSection";
import PageMeta from "@components/PageMeta";
import PreviewEditLink from "@components/PreviewEditLink";
import Slices from "@components/slices";
import { GlobalPageProps, slugify } from "@lib/utils";
import { GridContainer, GridWrapper } from "@theme/lib";
import dayjs from "dayjs";
import { ReactElement } from "react";
import { JsonLd } from "react-schemaorg";
import { Event } from "schema-dts";
import type { IconType } from "@components/Icon/types";
import { useMobile } from "@/lib/hooks/useMobile";
import { WindowWidth } from "@/lib/hooks/WindowWidth";

export type EventPageProps = ComponentProps<
  EventPageFragment
>;

export default function EventPage({ id, previewEditUrl, latestVersion, versionStatus, published, path, metatags, breadcrumbs, title, content, hero, informationSection, startDate, endDate, pageUrl }: EventPageProps & GlobalPageProps): ReactElement<any> {
  const heroProps = hero ? CreateHeroProps(hero) : null;
  const locationLink = informationSection?.tiles?.filter(item => item.label === 'Location');
  let location = "";
  if (locationLink) {
    const locationValue = locationLink[0]?.value;
    location = locationValue ? locationValue.replace(/(<([^>]+)>)/gi, "") : '';
  }

  const is960 = useMobile(960);
  const isMobile = useMobile(WindowWidth.Mobile);

  const IconMap: Record<string, IconType> = {
    'event-status': 'calendar',
    'event-type': 'calendar-check',
    'dates': 'calendar',
    'location': 'location',
    'duration': 'clock',
    'price': 'ticket',
    'accessibility': 'accessibility-3',
    'available-to': 'member-2',
  };

  const IconMapKeys = Object.keys(IconMap);

  return (
    <>
      <PageMeta
        metatags={metatags}
        breadcrumbs={breadcrumbs}
        path={path}
      />
      <JsonLd<Event>
        item={{
          "@context": "https://schema.org",
          "@type": "Event",
          "name": heroProps?.title || title,
          "description": metatags?.metaDescription || '',
          "image": metatags?.metaImage?.md,
          "startDate": startDate ? dayjs(startDate).format('YYYY-MM-DDTHH:mm') : undefined,
          "endDate": endDate ? dayjs(endDate).format('YYYY-MM-DDTHH:mm') : undefined,
          "location": location ? location : undefined,
        }}
      />
      {previewEditUrl &&
        <PreviewEditLink url={previewEditUrl} latestVersion={latestVersion} versionStatus={versionStatus} published={published} pageUrl={pageUrl}/>
      }
      {heroProps && <Hero {...heroProps} />}
      <main>
        {informationSection && (
          <GridContainer>
            <GridWrapper alignment={'full-width'}>
              {informationSection.tiles && informationSection.tiles.length > 0 && (
                <IconLegend
                  toggle={isMobile}
                  toggleItem="event-status"
                  toggleLabel="Event information"
                  ariaLabel="Event information"
                  grid={isMobile ? 1 : is960 ? 3 : 5}
                  iconWidth={37}
                  iconHeight={37}
                  items={[...informationSection.tiles]
                    .sort((a, b) => {
                      const aIndex = IconMapKeys.indexOf(slugify(a.label));
                      const bIndex = IconMapKeys.indexOf(slugify(b.label));
                      return (aIndex === -1 ? Infinity : aIndex) - (bIndex === -1 ? Infinity : bIndex);
                    })
                    .map(({ label, value }) => {
                      const slug = slugify(label);
                      const info = slug === 'event-status' && value
                        ? `<div class="icon-status icon-status-${slugify(value)}"></div>${value}`
                        : value ?? undefined;

                      return {
                        type: IconMap[slug] || '',
                        label,
                        info,
                      };
                    })}
                />
              )}
            </GridWrapper>
          </GridContainer>
        )}
        {content && <Slices {...content} />}
      </main>
    </>
  );
}
